// CROPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const CROPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const croStats = [
        { number: '88%', label: 'of online consumers are less likely to return after a poor experience' },
        { number: '3.5x', label: 'higher conversion rates with optimized user experiences' },
        { number: '70%', label: 'increase in customer satisfaction through improved UX design' },
        { number: '40%', label: 'reduction in bounce rates with enhanced user interfaces' }
    ];

    const croApproaches = [
        {
            icon: 'fas fa-search',
            title: 'Conversion Audit & Analytics Review',
            desc: 'Comprehensive website and digital funnel analysis using behavioral metrics, heatmaps, session data, and funnel analytics to identify conversion barriers and drop-off points.'
        },
        {
            icon: 'fas fa-brain',
            title: 'Hypothesis Development & UX Strategy',
            desc: 'Strategic hypothesis creation based on user behavior insights, UX recommendations for friction reduction, and messaging optimization to prioritize high-impact conversion improvements.'
        },
        {
            icon: 'fas fa-flask',
            title: 'A/B & Multivariate Testing',
            desc: 'Design and execution of controlled experiments including A/B testing, split testing, and multivariate testing with statistical validation and iterative optimization methodologies.'
        },
        {
            icon: 'fas fa-paint-brush',
            title: 'Conversion-Focused Design Enhancement',
            desc: 'Strategic redesign of landing pages, CTAs, forms, and user interfaces with optimized messaging, visual hierarchy, and persuasive elements to drive conversion actions.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Performance Tracking & Optimization',
            desc: 'Comprehensive KPI dashboards, behavioral trend analysis, conversion funnel monitoring, and data-driven optimization recommendations for continuous improvement.'
        },
        {
            icon: 'fas fa-user-check',
            title: 'User Experience & Journey Optimization',
            desc: 'End-to-end user journey mapping, friction point identification, customer experience enhancement, and conversion path optimization across all digital touchpoints.'
        }
    ];

    const benefits = [
        ['Increased Conversion Rates', 'Transform more website visitors into customers, leads, or subscribers without increasing traffic acquisition costs'],
        ['Enhanced Marketing ROI', 'Improve return on advertising spend by maximizing the value generated from existing traffic sources and campaigns'],
        ['Optimized User Experience', 'Create frictionless customer journeys that reduce abandonment and improve satisfaction across all digital interactions'],
        ['Data-Driven Decision Making', 'Base strategic improvements on quantitative insights and behavioral analytics rather than assumptions or guesswork'],
        ['Competitive Performance Advantage', 'Outperform competitors by systematically optimizing conversion paths and customer experience elements']
    ];

    const targetBusinesses = [
        'E-commerce brands seeking to increase sales conversions and average order value from existing website traffic',
        'SaaS platforms focused on improving free trial sign-ups, feature adoption, and customer activation rates',
        'B2B companies aiming to enhance lead generation, form completions, and qualified pipeline development',
        'Digital products and services looking to optimize user onboarding, retention, and lifetime value metrics',
        'Content publishers and media sites wanting to boost newsletter subscriptions and engagement conversions',
        'Any business with measurable digital traffic seeking to maximize revenue and customer acquisition efficiency'
    ];

    const croQuestions = [
        'What barriers prevent your website visitors from completing desired actions?',
        'How can you optimize user journeys to reduce friction and increase conversion rates?',
        'What design and messaging improvements would have the greatest impact on your conversions?',
        'How do you systematically test and validate optimization hypotheses with statistical confidence?',
        'What data insights reveal the most significant opportunities for conversion improvement?'
    ];

    const croTechniques = [
        'User Behavior Analysis & Heatmap Tracking',
        'A/B Testing & Multivariate Experimentation',
        'Conversion Funnel Optimization & Drop-off Analysis',
        'User Experience (UX) Research & Journey Mapping',
        'Statistical Analysis & Significance Testing',
        'Persuasive Design Principles & Conversion Psychology'
    ];

    const croOutcomes = [
        { outcome: 'Higher Conversion Rates', impact: 'More customers, leads, or sign-ups generated from the same volume of website traffic' },
        { outcome: 'Improved Marketing ROI', impact: 'Increased revenue generation without corresponding increases in advertising or acquisition spending' },
        { outcome: 'Enhanced User Experience', impact: 'Streamlined customer journeys that reduce friction, confusion, and abandonment rates' },
        { outcome: 'Actionable Customer Insights', impact: 'Data-driven understanding of user behavior patterns and conversion barriers' },
        { outcome: 'Competitive Market Advantage', impact: 'Superior digital performance and customer experience compared to industry competitors' }
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Conversion Rate Optimization Services</span>
                    </h1>
                    <p>Data-driven strategies that transform website traffic into customers, leads, and measurable business outcomes</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Conversion Rate Optimization (CRO) is the strategic process of improving your website, landing pages, and user experiences so that a higher percentage of your visitors take valuable actions — whether that's making a purchase, submitting a lead form, signing up for a service, or any other conversion that matters to your business.
                                </p>
                                <p className="sv-description">
                                    Reachtix's CRO services are designed to help brands maximise the value of their digital traffic and improve overall performance by boosting conversions without necessarily increasing traffic. In crowded online landscapes, traffic alone is not enough — converting a greater percentage of that traffic drives measurable revenue uplift and improves marketing efficiency.
                                </p>
                                <p className="sv-description">
                                    CRO focuses on understanding how users interact with your site, identifying barriers that prevent them from converting, and implementing strategic improvements that guide them toward desired outcomes. Our approach helps you turn visitors into customers through structured, insight-driven optimization that enhances both performance and user experience.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start CRO Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Explore CRO Services
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">CRO Performance Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {croStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is CRO Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Conversion Rate Optimization (CRO)?</h2>
                                <p className="sv-section-description">
                                    CRO is the systematic process of increasing the percentage of website visitors who complete desired actions through data analysis, user research, hypothesis testing, and strategic optimization of design, content, and user experience elements.
                                </p>
                                <p className="sv-section-description">
                                    This discipline combines quantitative analytics with qualitative insights to identify conversion barriers, optimize customer journeys, and implement improvements that drive measurable business outcomes — transforming website traffic into revenue, leads, and customer growth.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key CRO Questions We Address:</h4>
                                    {croQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Techniques Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Advanced CRO Techniques & Methodologies</h2>
                                <p className="sv-section-description">
                                    We employ sophisticated CRO methodologies and testing frameworks to identify optimization opportunities, validate improvement hypotheses, and implement changes that drive measurable conversion uplifts across all digital properties.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Core CRO Methodologies:</h4>
                                    {croTechniques.map((technique, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-cogs"></i>
                                            {technique}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why CRO Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why CRO Drives Sustainable Digital Growth</h2>
                                <p className="sv-section-description">
                                    CRO transforms website optimization from guesswork to science, enabling organizations to maximize the value of existing traffic, improve marketing efficiency, and build superior customer experiences that drive measurable business outcomes.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Revenue Optimization</h5>
                                        <p>Increase conversions and revenue without increasing traffic acquisition costs</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-user-check"></i>
                                        <h5>Experience Enhancement</h5>
                                        <p>Create frictionless customer journeys that improve satisfaction and retention</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-flask"></i>
                                        <h5>Data-Driven Decisions</h5>
                                        <p>Base strategic improvements on quantitative insights and statistical validation</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Competitive Advantage</h5>
                                        <p>Outperform competitors through systematic optimization and superior UX</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our CRO Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our CRO Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We combine analytics expertise, user experience research, behavioral insights, and structured testing methodologies to create comprehensive CRO strategies that deliver measurable conversion improvements and sustainable business growth.
                                </p>

                                <div className="sv-approaches-grid">
                                    {croApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* CRO Outcomes Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Measurable CRO Outcomes & Business Impact</h2>
                                <p className="sv-section-description mb-4">
                                    Our CRO strategies help brands transform website traffic into tangible business outcomes — delivering not just improved metrics, but meaningful commercial results that drive growth and competitive advantage.
                                </p>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Outcome</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {croOutcomes.map((outcome, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{outcome.outcome}</strong>
                                                    </td>
                                                    <td>{outcome.impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of CRO with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    CRO transforms your digital presence from a traffic destination to a conversion engine, enabling maximized revenue potential, improved marketing efficiency, enhanced customer experiences, and sustainable competitive advantage in crowded digital markets.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Conversion Rate Optimization?</h2>
                                <p className="sv-section-description">
                                    CRO delivers maximum value for organizations seeking to maximize the commercial impact of their digital traffic, improve marketing efficiency, and build superior customer experiences that drive measurable business outcomes:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're generating substantial website traffic or looking to improve the quality of visitor engagement, CRO unlocks greater commercial value from your digital presence and marketing investments.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner With Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Partner with Reachtix for CRO?</h2>
                                <p className="sv-section-description">
                                    At Reachtix, conversion optimization is both a scientific discipline and a strategic art. We blend quantitative analytics with customer behavior insights, UX expertise, and creative problem-solving to deliver optimization that doesn't just improve metrics — it drives meaningful business outcomes.
                                </p>
                                <p className="sv-section-description">
                                    What sets us apart is our data-first CRO methodology, structured hypothesis testing framework, integrated UX and performance design approach, and commitment to delivering measurable impact with transparent reporting. We help brands unlock growth by systematically converting more of the visitors they already attract.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Our CRO Expertise:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-database"></i>
                                        Data-First CRO Methodology with Statistical Rigor
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-flask"></i>
                                        Structured Hypothesis Testing & Experimental Validation
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-user-friends"></i>
                                        Integrated UX Research & Customer Behavior Insights
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-pie"></i>
                                        Transparent Impact Measurement & Performance Reporting
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Transform Website Visitors into Valuable Conversions?</h2>
                                <p className="sv-cta-description">
                                    Increasing traffic is only the first step — converting that traffic into measurable business results is where real impact happens. CRO unlocks the hidden potential in your existing website traffic, driving revenue growth without corresponding increases in acquisition costs.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat CRO as a strategic growth engine — not just technical optimization. Our specialists blend analytical rigor with customer insight to make your website a powerful driver of conversions, revenue, and sustainable business growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Start CRO Project
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Free CRO Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default CROPage;